'use client';
import React, { useState } from 'react';
import Button from '@/components/ui/Button';
import SafeHtml from '@/components/ui/SafeHtml';
import NutritionalInfoDisplay from '@/components/ui/NutritionalInfoDisplay';
import ResponsiveSpacing from '@/components/common/ResponsiveSpacing';
import { ProductDetail } from '@/types/product';
import { ProductNutritionalInfo } from '@/types/strapi';
import {
  normalizePrestaShopDescriptionHtml,
  productBodyTextWrapperClass,
  productDescriptionProseClass,
  productInfoProseClass,
} from '@/services/seo/productSeoHtml';

interface ProductInfoSectionProps {
  product: ProductDetail;
  nutritionalInfo?: ProductNutritionalInfo | null;
  className?: string;
}

const ProductInfoSection: React.FC<ProductInfoSectionProps> = ({
  product,
  nutritionalInfo,
  className = '',
}) => {
  const [selectedTab, setSelectedTab] = useState('description');
  const descriptionHtml = normalizePrestaShopDescriptionHtml(product.description ?? '');

  const nutritionContent =
    nutritionalInfo?.Informacion_Nutricional_Lista &&
    nutritionalInfo.Informacion_Nutricional_Lista.length > 0 ? (
      <NutritionalInfoDisplay data-component-id="src/components/sections/ProductInfoSection.tsx:33:6" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="33" data-component-file="ProductInfoSection.tsx" data-component-name="NutritionalInfoDisplay" data-component-content="%7B%22elementName%22%3A%22NutritionalInfoDisplay%22%7D" items={nutritionalInfo.Informacion_Nutricional_Lista} />
    ) : nutritionalInfo?.informacion_nutricional ? (
      <div data-component-id="src/components/sections/ProductInfoSection.tsx:35:6" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="35" data-component-file="ProductInfoSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22%5Bvar%3AproductBodyTextWrapperClass%5D%22%7D" className={productBodyTextWrapperClass}>
        <SafeHtml data-component-id="src/components/sections/ProductInfoSection.tsx:36:8" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="36" data-component-file="ProductInfoSection.tsx" data-component-name="SafeHtml" data-component-content="%7B%22elementName%22%3A%22SafeHtml%22%2C%22className%22%3A%22%5Bvar%3AproductInfoProseClass%5D%22%7D" html={nutritionalInfo.informacion_nutricional} className={productInfoProseClass} />
      </div>
    ) : (
      <p data-component-id="src/components/sections/ProductInfoSection.tsx:39:6" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="39" data-component-file="ProductInfoSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22%5Bvar%3AproductBodyTextWrapperClass%5D%22%2C%22textContent%22%3A%22Informaci%C3%B3n%20nutricional%20no%20disponible.%22%7D" className={productBodyTextWrapperClass}>
        Información nutricional no disponible.
      </p>
    );

  const ingredientsContent =
    nutritionalInfo?.ingredientes && nutritionalInfo.ingredientes.trim() !== '' ? (
      <div data-component-id="src/components/sections/ProductInfoSection.tsx:46:6" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="46" data-component-file="ProductInfoSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22%5Bvar%3AproductBodyTextWrapperClass%5D%22%7D" className={productBodyTextWrapperClass}>
        <SafeHtml data-component-id="src/components/sections/ProductInfoSection.tsx:47:8" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="47" data-component-file="ProductInfoSection.tsx" data-component-name="SafeHtml" data-component-content="%7B%22elementName%22%3A%22SafeHtml%22%2C%22className%22%3A%22%5Bvar%3AproductInfoProseClass%5D%22%7D" html={nutritionalInfo.ingredientes} className={productInfoProseClass} />
      </div>
    ) : (
      <p data-component-id="src/components/sections/ProductInfoSection.tsx:50:6" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="50" data-component-file="ProductInfoSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22%5Bvar%3AproductBodyTextWrapperClass%5D%22%2C%22textContent%22%3A%22Lista%20de%20ingredientes%20no%20disponible.%22%7D" className={productBodyTextWrapperClass}>
        Lista de ingredientes no disponible.
      </p>
    );

  const panels = [
    {
      id: 'description',
      label: 'DESCRIPCIÓN',
      heading: 'Descripción',
      content: (
        <div data-component-id="src/components/sections/ProductInfoSection.tsx:61:8" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="61" data-component-file="ProductInfoSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22%5Bvar%3AproductBodyTextWrapperClass%5D%22%7D" className={productBodyTextWrapperClass}>
          <SafeHtml data-component-id="src/components/sections/ProductInfoSection.tsx:62:10" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="62" data-component-file="ProductInfoSection.tsx" data-component-name="SafeHtml" data-component-content="%7B%22elementName%22%3A%22SafeHtml%22%2C%22className%22%3A%22%5Bvar%3AproductDescriptionProseClass%5D%22%7D" html={descriptionHtml} className={productDescriptionProseClass} />
        </div>
      ),
    },
    {
      id: 'nutrition',
      label: 'INFORMACIÓN NUTRICIONAL',
      heading: 'Información nutricional',
      content: nutritionContent,
    },
    {
      id: 'ingredients',
      label: 'INGREDIENTES',
      heading: 'Ingredientes',
      content: ingredientsContent,
    },
  ];

  return (
    <ResponsiveSpacing data-component-id="src/components/sections/ProductInfoSection.tsx:81:4" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="81" data-component-file="ProductInfoSection.tsx" data-component-name="ResponsiveSpacing" data-component-content="%7B%22elementName%22%3A%22ResponsiveSpacing%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" padding="md" className={className}>
      <div data-component-id="src/components/sections/ProductInfoSection.tsx:82:6" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="82" data-component-file="ProductInfoSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20gap-3%20sm%3Agap-5%20mb-5%22%7D" className="flex flex-col sm:flex-row gap-3 sm:gap-5 mb-5">
        {panels.map((panel) => (
          <Button data-component-id="src/components/sections/ProductInfoSection.tsx:84:10" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="84" data-component-file="ProductInfoSection.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%7D"
            key={panel.id}
            variant={selectedTab === panel.id ? 'primary' : 'secondary'}
            onClick={() => setSelectedTab(panel.id)}
            className={`w-full sm:w-[457px] h-[54px] font-tt-norms-bold focus:ring-0 focus:outline-none focus:ring-opacity-0 focus:ring-offset-0 ${
              selectedTab === panel.id
                ? 'bg-[#893B1C]/[0.19] text-[#3d1c1a]'
                : 'bg-[#893B1C]/[0.07] text-global-1'
            }`}
            style={{ boxShadow: 'none' }}
          >
            {panel.label}
          </Button>
        ))}
      </div>

      <div data-component-id="src/components/sections/ProductInfoSection.tsx:100:6" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="100" data-component-file="ProductInfoSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-%5B%23893B1C%5D%2F%5B0.19%5D%20rounded-%5B10px%5D%20p-11%22%7D" className="bg-[#893B1C]/[0.19] rounded-[10px] p-11">
        {panels.map((panel) => (
          <div data-component-id="src/components/sections/ProductInfoSection.tsx:102:10" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="102" data-component-file="ProductInfoSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            key={panel.id}
            id={`product-tabpanel-${panel.id}`}
            role="tabpanel"
            hidden={selectedTab !== panel.id}
            className={selectedTab !== panel.id ? 'hidden' : undefined}
          >
            <h2 data-component-id="src/components/sections/ProductInfoSection.tsx:109:12" data-component-path="src/components/sections/ProductInfoSection.tsx" data-component-line="109" data-component-file="ProductInfoSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22sr-only%22%7D" className="sr-only">{panel.heading}</h2>
            {panel.content}
          </div>
        ))}
      </div>
    </ResponsiveSpacing>
  );
};

export default ProductInfoSection;


